import React, { useState, useEffect } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";

const TypeNameAntivirusMasterList = () => {
    const { http } = AuthUser();
    const [typeNames, setTypeNames] = useState([]);
    const [formData, setFormData] = useState({
        name: ''
    });
    const [editingTypeName, setEditingTypeName] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchTypeNames();
    }, []);

    const fetchTypeNames = async () => {
        try {
            const response = await http.get('/type_name_antivirus_master');
            if (response.data.success) {
                setTypeNames(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching type names:", error);
            toast.error("Error loading type names");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || formData.name.trim() === '') {
            toast.error("Name is required");
            return;
        }
        
        setIsLoading(true);

        try {
            let response;
            if (editingTypeName) {
                response = await http.put(`/type_name_antivirus_master/${editingTypeName.id}`, formData);
                toast.success("Type name updated successfully!");
            } else {
                response = await http.post('/type_name_antivirus_master', formData);
                toast.success("Type name created successfully!");
            }

            if (response.data.success) {
                setShowModal(false);
                setFormData({ name: '' });
                setEditingTypeName(null);
                fetchTypeNames();
            }
        } catch (error) {
            console.error("Error saving type name:", error);
            const errorMessage = error.response?.data?.message || "Error saving type name";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (typeName) => {
        setEditingTypeName(typeName);
        setFormData({
            name: typeName.name
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this type name?")) {
            try {
                await http.delete(`/type_name_antivirus_master/${id}`);
                toast.success("Type name deleted successfully!");
                fetchTypeNames();
            } catch (error) {
                console.error("Error deleting type name:", error);
                toast.error("Error deleting type name");
            }
        }
    };

    const handleAddNew = () => {
        setEditingTypeName(null);
        setFormData({ name: '' });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingTypeName(null);
        setFormData({ name: '' });
    };

    // Filter type names based on search term
    const filteredTypeNames = typeNames.filter(typeName =>
        typeName.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="container-fluid mt-4">
            <Toaster />
            
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Type Name Master</h5>
                            <button className="btn btn-sm btn-primary" onClick={handleAddNew}>
                                <i className="fas fa-plus-circle me-1"></i> Add Type Name
                            </button>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="table-responsive">
                                <table className="table table-striped table-bordered">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Name</th>
                                            <th>Created At</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTypeNames.length > 0 ? (
                                            filteredTypeNames.map((typeName) => (
                                                <tr key={typeName.id}>
                                                    <td>{typeName.id}</td>
                                                    <td>{typeName.name}</td>
                                                    <td>{new Date(typeName.created_at).toLocaleDateString()}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <button
                                                            className="btn btn-sm btn-warning me-1"
                                                            onClick={() => handleEdit(typeName)}
                                                            style={{ fontSize: '11px', padding: '5px 10px' }}
                                                            title="Edit"
                                                        >
                                                            <i className="fas fa-pen"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDelete(typeName.id)}
                                                            style={{ fontSize: '11px', padding: '5px 10px' }}
                                                            title="Delete"
                                                        >
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center">No type names found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal for Add/Edit */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingTypeName ? 'Edit Type Name' : 'Add New Type Name'}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                        Close
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            'Save'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TypeNameAntivirusMasterList;

